import type { PropsWithChildren } from "react";

import { FAIconName, Icon, Text } from "@/ui/core";
import { TextAttributesMapper } from "@/ui/core/Text";
import { Box, Circle, HStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { isAndroid } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

interface StepCounterProps extends ViewWithTestID {
  currentStep: number;
  totalSteps: number;
}

type StepState = "completed" | "current" | "default";

interface StepStyle {
  backgroundColor: ColorToken;
  borderColor: ColorToken;
  color: ColorToken;
}

const StepStyleMapper: Record<StepState, StepStyle> = {
  completed: {
    backgroundColor: ColorToken.Primary100,
    borderColor: ColorToken.Primary100,
    color: ColorToken.NeutralBackgroundPrimary,
  },
  current: {
    backgroundColor: ColorToken.Primary90Variant,
    borderColor: ColorToken.Primary90Variant,
    color: ColorToken.NeutralBackgroundPrimary,
  },
  default: {
    backgroundColor: ColorToken.NeutralBackgroundPrimary,
    borderColor: ColorToken.NeutralPrimary,
    color: ColorToken.NeutralPrimary,
  },
};

const getStepState = ({
  isCompletedStep,
  isCurrentStep,
}: {
  isCompletedStep: boolean;
  isCurrentStep: boolean;
}): StepState => {
  if (isCurrentStep) {
    return "current";
  }
  if (isCompletedStep) {
    return "completed";
  }
  return "default";
};

// Android renders text lineHeight differently, so we need to manually center the text
const StepNumberContainer = ({ children }: PropsWithChildren) => {
  if (isAndroid()) {
    return (
      <Box
        alignItems="center"
        height={TextAttributesMapper.overlineMedium.fontSize}
        justifyContent="center"
      >
        {children}
      </Box>
    );
  }
  return <Box>{children}</Box>;
};

const StepItem = ({
  currentStep,
  isLastItem,
  step,
  testID,
}: {
  currentStep: number;
  isLastItem: boolean;
  step: number;
  testID: string;
}) => {
  const isCurrentStep = step === currentStep;
  const isCompletedStep = step < currentStep;

  const stepState = getStepState({ isCompletedStep, isCurrentStep });
  const { backgroundColor, borderColor, color } = StepStyleMapper[stepState];

  return (
    <HStack alignItems="center" flexGrow={isLastItem ? 0 : 1} gap={6}>
      <Circle
        aria-current={isCurrentStep ? currentStep : undefined}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        borderWidth={1}
        height={18}
        width={18}
      >
        {isCompletedStep ? (
          <Icon
            accessibilityLabel={`Step ${step} completed`}
            color={color}
            name={FAIconName.Check}
            size="xs"
            testID={joinTestIDs(testID, "completed")}
          />
        ) : (
          <StepNumberContainer>
            <Text
              color={color}
              testID={joinTestIDs(testID, step.toString())}
              variant="overlineMedium"
            >
              {step}
            </Text>
          </StepNumberContainer>
        )}
      </Circle>
      {!isLastItem && (
        <Box
          backgroundColor={
            isCompletedStep ? ColorToken.Primary100 : ColorToken.NeutralLine
          }
          borderRadius={1}
          flexGrow={1}
          height={1.5}
          testID={joinTestIDs(testID, `step-${step}-separator`)}
        />
      )}
    </HStack>
  );
};

const StepCounter = ({ currentStep, testID, totalSteps }: StepCounterProps) => {
  return (
    <HStack flexWrap="wrap" gap={6} testID={testID}>
      {/* eslint-disable-next-line max-params */}
      {Array.from({ length: totalSteps }).map((_, index) => (
        <StepItem
          currentStep={currentStep}
          isLastItem={index === totalSteps - 1}
          key={joinTestIDs(testID, index.toString())}
          step={index + 1}
          testID={testID}
        />
      ))}
    </HStack>
  );
};

export default StepCounter;
export type { StepCounterProps };
